<script>
import NtvInput from '../Input';
import NtvIcon from '../Icon';

export default {
  name: 'NtvSearchInput',
  components: {
    NtvInput,
    NtvIcon
  },
  props: {
    /**
     * Label that is shown on top of the input
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Value of input
     */
    value: {
      type: [String, Number, Boolean],
      default: ''
    },
    /**
     * Placeholder shown if empty
     */
    placeholder: {
      type: String,
      default: ''
    },
    /**
     * Width of input
     */
    width: {
      type: String,
      default: '100%'
    },
    /**
     * The error message displayed under input
     */
    errorMessage: {
      type: String,
      default: ''
    },
    /**
     * The message displayed in the info bubble.
     */
    infoMessage: {
      type: String,
      default: ''
    },
    /**
     * Required flag
     */
    isRequired: {
      type: Boolean,
      default: false
    },
    /**
     * Disable input
     */
    isDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Set to read only
     */
    isReadOnly: {
      type: Boolean,
      default: false
    },
    /**
     * Remove the border from the Input
     * @type {Boolean}
     */
    isRemoveBorder: {
      type: Boolean,
      default: false
    },
    /**
     * If the x icon is to be shown when input has some text
     * @type {Boolean}
     */
    showClearIcon: {
      type: Boolean,
      default: true
    },
    /**
     * If search icon is to be shown
     * @type {Boolean}
     */
    showSearchIcon: {
      type: Boolean,
      default: true
    }
  },

  data: function() {
    return {
      currValue: ''
    };
  },

  computed: {
    showClose() {
      return (
        this.currValue.length > 0 &&
        !this.isReadOnly &&
        !this.isDisabled &&
        this.showClearIcon
      );
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.currValue = value;
      }
    }
  },

  methods: {
    clearValue() {
      this.currValue = '';

      this.$refs.input.clearValue();
    },
    // For parent wrappers to use.
    focusInput() {
      this.$refs.input.$refs.input.focus();
    },
    onInput(inputVal, evt) {
      this.currValue = inputVal;

      /**
       * Input event handler.
       * Parameters: (val: String, evt: Event)
       * @event input
       */
      this.$emit('input', inputVal, evt);
    },
    onChange(inputVal, evt) {
      /**
       * Change event handler.
       * Parameters: (val: String, evt: Event)
       * @event change
       */
      this.$emit('change', inputVal, evt);
    },
    onBlur(value, evt) {
      /**
       * Blur event handler. Parameters: (val: String, evt: Event)
       * @event blur
       */
      this.$emit('blur', value, evt);
    },
    onFocus(value, evt) {
      /**
       * Focus event handler. Parameters: (val: String, evt: Event)
       * @event blur
       */
      this.$emit('focus', value, evt);
    },
    onKeydown: function(evt) {
      /**
       * Keydown event handler.
       * Parameters: (evt: Event)
       * @event keydown
       */
      this.$emit('keydown', evt);
    }
  }
};
</script>

<template>
  <div class="ntv-search-input">
    <ntv-input
      :class="{'ntv-search-input__no_search-icon': !showSearchIcon}"
      ref="input"
      v-bind="{
        label, 
        placeholder, 
        width, 
        errorMessage,
        infoMessage,
        isRequired, 
        isDisabled, 
        isReadOnly, 
        isRemoveBorder
      }"
      :value="currValue"
      @change="onChange"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @keydown="onKeydown"
    >
      <span
        class="text-default"
        :class="{'ntv-search-input__search-icon': showSearchIcon}"
        v-if="showSearchIcon"
      >
        <ntv-icon
          name="search"
          size="sm"
          use-fa
        />
      </span>

      <!-- 
        Wrap icon component in span so events can be attached since 
        ntv-icon renders an svg tag for font awesome icons.
      -->
      <span
        class="ntv-search-input__close-icon cursor-pointer"
        @click="clearValue"
        @mousedown.prevent
        v-if="showClose"
      >
        <ntv-icon
          use-fa
          name="times"
        />
      </span>
    </ntv-input>
    <slot />
  </div>
</template>

<style lang="scss">
.ntv-search-input {
  .ntv-input__input {
    padding-left: 32px;
    padding-right: 32px;
  }
}

.ntv-search-input__no_search-icon {
  .ntv-input__input {
    padding-left: 15px;
    padding-right: 15px;
  }
}

.ntv-search-input__search-icon {
  width: 13px;
  position: absolute;
  bottom: 50%;
  left: 13px;
  transform: translateY(50%);
}

.ntv-search-input__close-icon {
  width: 10px;
  position: absolute;
  bottom: 50%;
  right: 15px;
  transform: translateY(50%);
  cursor: pointer;
}
</style>
